import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { ResendVerificationPage } from './pages/ResendVerificationPage';
import { AuthCallback } from './pages/AuthCallback';
import { AuthError } from './pages/AuthError';
import { PaymentPage } from './pages/PaymentPage';
import { PaymentSuccessPage } from './pages/PaymentSuccessPage';
import { PaymentProcessingPage } from './pages/PaymentProcessingPage';
import { SessionsPage } from './pages/SessionsPage';
import { ConnectedAccountsPage } from './pages/settings/ConnectedAccountsPage';
import { HomePage } from './pages/HomePage';
import { Dashboard } from './pages/Dashboard';
import { TherapistDashboardPage } from './pages/dashboard/TherapistDashboardPage';
import { TherapistAppointmentDetailPage } from './pages/dashboard/TherapistAppointmentDetailPage';
import { TherapistSettingsPage } from './pages/TherapistSettingsPage';
import { ServicesPage } from './pages/ServicesPage';
import { BookingPage } from './pages/BookingPage';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { WelcomePage } from './pages/WelcomePage';
import { TherapistsPage } from './pages/TherapistsPage';
import { ContactPage } from './pages/ContactPage';
import { BookingDetailPage } from './pages/BookingDetailPage';
import { SettingsPage } from './pages/SettingsPage';
import { NotificationPreferencesPage } from './pages/NotificationPreferencesPage';
import { ServiceDetailPage } from './pages/ServiceDetailPage';
import { TherapistProfilePage } from './pages/TherapistProfilePage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { TermsOfServicePage } from './pages/TermsOfServicePage';
import { StripeProvider } from './contexts/StripeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider, ToastContainer } from './components/ui/Toast';
import { Layout } from './components/layout';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ScrollToTop } from './components/ScrollToTop';

// Admin Pages
import { AdminDashboardPage } from './pages/admin/AdminDashboardPage';
import { TherapistsPage as AdminTherapistsPage } from './pages/admin/TherapistsPage';
import { TherapistsListPage } from './pages/admin/TherapistsListPage';
import { TherapistDetailPage } from './pages/admin/TherapistDetailPage';
import { TherapistCreatePage } from './pages/admin/TherapistCreatePage';
import { ServicesPage as AdminServicesPage } from './pages/admin/ServicesPage';
import { BookingsPage as AdminBookingsPage } from './pages/admin/BookingsPage';
import { ReconciliationDashboardPage } from './pages/admin/ReconciliationDashboardPage';
import { CustomersPage } from './pages/admin/CustomersPage';
import { CustomerDetailPage } from './pages/admin/CustomerDetailPage';
import { SettingsPage as AdminSettingsPage } from './pages/admin/SettingsPage';
import { AuditLogsPage } from './pages/admin/AuditLogsPage';
import { AnalyticsDashboardPage } from './pages/admin/AnalyticsDashboardPage';

// Protected route wrapper - redirects to login if not authenticated
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  return <>{children}</>;
}

// Admin route wrapper - redirects non-admin users to dashboard
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, isAdmin } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (!isAdmin()) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

// Therapist route wrapper - redirects non-therapist/non-admin users to dashboard
function TherapistRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user, isAdmin } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (user?.role !== 'THERAPIST' && !isAdmin()) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

// Build version - helps verify deployment
const BUILD_VERSION = '2026-01-31-v2';
if (typeof window !== 'undefined') {
  (window as unknown as { __BUILD_VERSION__: string }).__BUILD_VERSION__ = BUILD_VERSION;
  console.log(`[Massatherapy] Build: ${BUILD_VERSION}`);
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
      <AuthProvider>
          <ToastProvider>
            <BrowserRouter>
              <ScrollToTop />
              <Routes>
              {/* Auth routes - no layout (have their own design) */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/resend-verification" element={<ResendVerificationPage />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/auth/error" element={<AuthError />} />

              {/* Payment routes (protected) - with layout */}
              <Route
                path="/payment/:bookingId"
                element={
                  <ProtectedRoute>
                    <StripeProvider>
                      <Layout>
                        <PaymentPage />
                      </Layout>
                    </StripeProvider>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payment/success"
                element={
                  <Layout>
                    <PaymentSuccessPage />
                  </Layout>
                }
              />
              <Route
                path="/payment/processing"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <PaymentProcessingPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Settings routes (protected) - with layout */}
              <Route
                path="/settings/sessions"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <SessionsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings/accounts"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ConnectedAccountsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings/notifications"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <NotificationPreferencesPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sessions"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <SessionsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Services routes (public) - with layout */}
              <Route
                path="/services"
                element={
                  <Layout>
                    <ServicesPage />
                  </Layout>
                }
              />
              <Route
                path="/services/:slug"
                element={
                  <Layout>
                    <ServiceDetailPage />
                  </Layout>
                }
              />

              {/* Booking route (protected) - with layout */}
              <Route
                path="/booking"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <BookingPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Dashboard (protected) - no Layout wrapper (has its own) */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bookings"
                element={<Navigate to="/dashboard?section=bookings" replace />}
              />
              <Route
                path="/bookings/:id"
                element={
                  <ProtectedRoute>
                    <BookingDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-appointments"
                element={<Navigate to="/dashboard?section=history" replace />}
              />
              <Route
                path="/book"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <BookingPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Therapist Dashboard (therapist/admin only) - no layout wrapper (has its own) */}
              <Route
                path="/therapist/dashboard"
                element={
                  <TherapistRoute>
                    <TherapistDashboardPage />
                  </TherapistRoute>
                }
              />
              <Route
                path="/therapist/appointments/:id"
                element={
                  <TherapistRoute>
                    <TherapistAppointmentDetailPage />
                  </TherapistRoute>
                }
              />
              <Route
                path="/therapist/settings"
                element={
                  <TherapistRoute>
                    <TherapistSettingsPage />
                  </TherapistRoute>
                }
              />

              {/* Public pages - with layout handled internally */}
              <Route path="/welcome" element={<WelcomePage />} />
              <Route path="/therapists" element={<TherapistsPage />} />
              <Route path="/therapists/:id" element={<TherapistProfilePage />} />
              <Route path="/contact" element={<ContactPage />} />

              {/* Profile and Settings routes */}
              <Route
                path="/profile"
                element={<Navigate to="/dashboard?section=profile" replace />}
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <SettingsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Home page - uses Layout internally with hasHeaderPadding={false} */}
              <Route path="/" element={<HomePage />} />

              {/* Admin routes (admin only) - no Layout (has own AdminLayout) */}
              <Route
                path="/admin"
                element={
                  <AdminRoute>
                    <AdminDashboardPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/therapists"
                element={
                  <AdminRoute>
                    <AdminTherapistsPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/therapists/list"
                element={
                  <AdminRoute>
                    <TherapistsListPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/therapists/new"
                element={
                  <AdminRoute>
                    <TherapistCreatePage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/therapists/:id"
                element={
                  <AdminRoute>
                    <TherapistDetailPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/services"
                element={
                  <AdminRoute>
                    <AdminServicesPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/bookings"
                element={
                  <AdminRoute>
                    <AdminBookingsPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/reconciliation"
                element={
                  <AdminRoute>
                    <ReconciliationDashboardPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/customers"
                element={
                  <AdminRoute>
                    <CustomersPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/customers/:id"
                element={
                  <AdminRoute>
                    <CustomerDetailPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/settings"
                element={
                  <AdminRoute>
                    <AdminSettingsPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/audit-logs"
                element={
                  <AdminRoute>
                    <AuditLogsPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/analytics"
                element={
                  <AdminRoute>
                    <AnalyticsDashboardPage />
                  </AdminRoute>
                }
              />

              {/* Legal pages (public) - with layout */}
              <Route
                path="/privacy"
                element={
                  <Layout>
                    <PrivacyPolicyPage />
                  </Layout>
                }
              />
              <Route
                path="/terms"
                element={
                  <Layout>
                    <TermsOfServicePage />
                  </Layout>
                }
              />

              {/* 404 Not Found - catch all unmatched routes */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            </BrowserRouter>
            <ToastContainer />
          </ToastProvider>
      </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
